import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    IconButton,
    Typography,
    Divider,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Confirmación genérica para las acciones de estado de una recogida
 *
 * Se parametriza en vez de duplicar un modal por acción: cancelar y recepcionar
 * comparten forma y solo cambian el texto, el icono y el color del botón.
 */
export default function ModalConfirmarAccion({
    open,
    recogida,
    titulo,
    tituloMobile,
    mensaje,
    aviso,
    confirmLabel,
    confirmColor = "#cc071e",
    icon: Icon,
    onConfirm,
    onCancel,
    loading = false,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onCancel}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    m: isMobile ? 0 : 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {Icon && <Icon sx={{ color: confirmColor, fontSize: { xs: 20, sm: 24 } }} />}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                        {isMobile && tituloMobile ? tituloMobile : titulo}
                    </Typography>
                </Box>

                <IconButton onClick={onCancel} disabled={loading} size="small">
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : "initial",
                }}
            >
                <Typography sx={{ fontSize: 15, color: "#495057" }}>{mensaje}</Typography>

                {recogida && (
                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#f8f9fa", borderRadius: 1 }}>
                        <Typography sx={{ fontSize: 13, color: "#6c757d" }}>Ticket</Typography>
                        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#212529" }}>
                            {recogida.numeroTicket}
                        </Typography>
                    </Box>
                )}

                {aviso && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {aviso}
                    </Alert>
                )}
            </DialogContent>

            <Divider />

            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column-reverse", sm: "row" },
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onCancel}
                    fullWidth={isMobile}
                    disabled={loading}
                    sx={{
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        textTransform: "none",
                    }}
                >
                    Volver
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    fullWidth={isMobile}
                    disabled={loading}
                    startIcon={
                        loading ? (
                            <CircularProgress size={16} sx={{ color: "#fff" }} />
                        ) : (
                            Icon && <Icon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        )
                    }
                    sx={{
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: confirmColor,
                        "&:hover": { backgroundColor: confirmColor, filter: "brightness(0.9)" },
                        "&:disabled": { backgroundColor: "#6c757d", color: "#fff" },
                    }}
                >
                    {loading ? "Procesando..." : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
